import classNames from "classnames"
import { sumBy, map } from "lodash"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import { List, Paper } from "@mui/material"
import Dropzone from "react-dropzone"
import FileItem from "../file-item"
import "./style.scss"

export default function UploadStep({
  removeFile,
  files,
  addFiles,
  addNotification,
  uploadConfig,
  type = "portfolio",
  legend,
  docLink,
}) {
  function onDrop(acceptedFiles, rejectedFiles) {
    const currentSize = sumBy(files, ({ file }) => file.size)
    const newFilesSize = sumBy(acceptedFiles, file => file.size)
    const rejectedFileSize = sumBy(rejectedFiles, file => file.size)
    const displayMaxSize = uploadConfig.maxSize / 1000000

    if ((currentSize + (newFilesSize || rejectedFileSize)) > uploadConfig.maxSize) {
      addNotification("error", null, `The ${pluralize("file", (acceptedFiles.length || rejectedFiles.length) + files.length)} you are attempting to upload exceed the ${displayMaxSize} MB limit for a single upload. Please retry your upload with smaller files.`)
    } else {
      if (rejectedFiles.length > 0) {
        const errors = map(rejectedFiles, ({ file }) => {
          let error

          if (!file.type.match(uploadConfig.matchFormat)) {
            error = "is in an unsupported format."
          } else if (file.size > uploadConfig.maxSize) {
            error = `exceeds the ${displayMaxSize} MB limit for a single upload`
          } else {
            error = "could not be added."
          }

          return `${file.name} ${error}`
        })

        addNotification("error", null, `${errors.length} ${pluralize("file", errors.length)} will not be uploaded: ${errors.join(", ")}`)
      }

      if (acceptedFiles.length > 0) {
        addFiles(acceptedFiles)
      }
    }
  }

  return <div className="upload-step-wrapper">
    <div className="upload-step">
      <Dropzone onDrop={onDrop} accept={uploadConfig.acceptFormat} maxSize={uploadConfig.maxSize}>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div {...getRootProps()} className={classNames("dropzone-container", { "dropzone--isActive": isDragActive })}>
            <Paper square className="dropzone">
              <input {...getInputProps()} />
              {
                isDragActive
                  ? <p>Drop files here...</p>
                  : <>
                    <p>Drag files here or <span className="browse-link">browse</span></p>
                    <p className="browse-legend">
                      You can only upload <strong>XLS, XLSX, CSV</strong> files for this {type}.
                    </p>
                  </>
              }
            </Paper>
          </div>
        )}
      </Dropzone>
      <div className="upload-files-list">
        <Paper square elevation={1}>
          <List disablePadding>
            {files.map((file, i) =>
              <FileItem divider={i < files.length - 1} key={i} removeFile={removeFile} file={file} />,
            )}
          </List>
        </Paper>
      </div>
    </div>
    <div className="upload-legend">
      Please upload your data in Common Position Format. <a href={docLink} target="blank">Learn More</a>
    </div>
    {legend && <div className="upload-legend">{legend}</div>}
  </div>
}

UploadStep.propTypes = {
  removeFile: PropTypes.func.isRequired,
  files: PropTypes.array.isRequired,
  addFiles: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  uploadConfig: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  legend: PropTypes.node,
  docLink: PropTypes.string.isRequired,
}
